import React, { useState } from 'react';
import { AmountInput } from '../common/AmountInput';
import {
  Building,
  MapPin,
  Users,
  ChevronRight,
  Plus,
  Shield,
  Layers,
  ArrowRight,
  TrendingUp,
  FolderTree,
  UserCheck,
  Percent,
  CheckCircle2,
  Edit,
  Building2,
  Network,
  Trash2,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { Bank, RecoveryDepartment, Branch, User } from '../../types';
import { zonesOfBank } from '../../utils/hierarchy';

export const HierarchyManagementSection: React.FC = () => {
  const {
    currentUser,
    banks,
    zones,
    recoveryDepartments,
    branches,
    users,
    addBank,
    updateBank,
    addRecoveryDepartment,
    updateRecoveryDepartment,
    addBranch,
    updateBranch,
    addZone,
    deleteBank,
    deleteZone,
    deleteBranch,
    deleteRecoveryDepartment,
  } = useSRMS();

  /**
   * DELETING A LEVEL OF THE HIERARCHY.
   *
   * There was no delete here at all, so a branch created under the wrong
   * bank stayed there for good. Administrators only, and every one of these
   * refuses while anything still depends on the record -- the context
   * returns the counts and they are shown as they come back.
   *
   * A bank is the one that takes the whole structure with it, so it asks for
   * the name to be typed rather than a yes/no nobody reads.
   */
  const isAdminActor =
    currentUser?.role === 'admin' || (currentUser?.role as string) === 'super_admin';

  const runDelete = (
    label: string,
    fn: () => { success: boolean; message: string },
    typeToConfirm?: string
  ) => {
    if (typeToConfirm) {
      const typed = window.prompt(
        `Deleting ${label} cannot be undone.\n\nType the name exactly to confirm:`,
        ''
      );
      if (typed === null) return;
      if (typed.trim().toLowerCase() !== typeToConfirm.trim().toLowerCase()) {
        window.alert('That did not match. Nothing was deleted.');
        return;
      }
    } else if (!window.confirm(`Delete ${label}? This cannot be undone.`)) {
      return;
    }
    const res = fn();
    window.alert(res.message);
  };

  /**
   * DOES THIS PERSON WORK AT THIS BRANCH?
   *
   * THE BUG THIS FIXES. It used to be `u.branch === br.name` -- the branch
   * NAME and nothing else. Bank of Maharashtra has a Bidkin branch and
   * Central Bank of India has a Bidkin branch, so the agent posted to one
   * was listed under BOTH, and so was the branch manager. That is what
   * Ashish saw: his Bank of Maharashtra agent appearing under Central Bank
   * of India Bidkin.
   *
   * Now, in order:
   *
   *   1. The branch's own id, when the user record carries one. Nothing can
   *      confuse that, whatever anybody renames. New and edited users store
   *      it; this is what makes the problem go away for good.
   *   2. Otherwise the name AND the bank together, so two branches sharing a
   *      name stay separate.
   *
   * A user with no bank recorded still matches on the name alone -- those
   * are older records, and dropping them would empty branches that are
   * correctly staffed. They stop being ambiguous the moment the user is
   * edited and saved.
   */
  const branchKey = (v?: string | null) => (v || '').trim().toLowerCase();

  const belongsToBranch = (u: User, br: Branch): boolean => {
    const theirBranchIds = [
      (u as any).branchId,
      ...(((u as any).branchIds as string[] | undefined) || []),
    ].filter(Boolean);
    if (theirBranchIds.length > 0) return theirBranchIds.includes(br.id);

    const names = [u.branch, ...((u.branches as string[] | undefined) || [])]
      .map(branchKey)
      .filter(Boolean);
    if (!names.includes(branchKey(br.name))) return false;

    const theirBank = branchKey(u.bank);
    const branchBank = branchKey(br.bankName);
    // No bank on either side: nothing to disagree about.
    if (!theirBank || !branchBank) return true;
    if (theirBank === branchBank) return true;
    // A multi-bank user counts if this branch's bank is one of theirs.
    return (u.banks || []).map(branchKey).includes(branchBank);
  };

  // Active view tab: 'tree' | 'banks' | 'departments' | 'branches'
  const [activeTab, setActiveTab] = useState<'tree' | 'banks' | 'departments' | 'branches'>('tree');

  // Selected Bank for drill-down in tree view
  const [selectedBankId, setSelectedBankId] = useState<string>(banks[0]?.id || '');

  // Bank Form State
  const [isAddBankOpen, setIsAddBankOpen] = useState(false);
  const [newBankName, setNewBankName] = useState('');
  const [newBankCode, setNewBankCode] = useState('');
  // 0 means blank: the bank is saved with no single percentage (banks can have several commission types).
  const [newBankRate, setNewBankRate] = useState(0);
  const [newBankDesc, setNewBankDesc] = useState('');

  // Department Form State
  const [isAddDeptOpen, setIsAddDeptOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptCode, setNewDeptCode] = useState('');
  const [newDeptBankName, setNewDeptBankName] = useState('');
  const [newDeptZoneName, setNewDeptZoneName] = useState('');
  const [newDeptHead, setNewDeptHead] = useState('');
  const [newDeptDesc, setNewDeptDesc] = useState('');

  // Zonal Office Form State
  //
  // There was no way to create a Zonal Office at all. The hierarchy diagram
  // showed "2. Zone" as a step, but nothing could add one -- every zone in the
  // app arrived from demo data or an Excel import, which is why the zone
  // dropdown on User Creation could never offer the zone actually needed.
  const [isAddZoneOpen, setIsAddZoneOpen] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneCode, setNewZoneCode] = useState('');
  const [newZoneHead, setNewZoneHead] = useState('');
  const [newZoneBank, setNewZoneBank] = useState('');

  // Branch Form State
  const [isAddBranchOpen, setIsAddBranchOpen] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [newBranchCode, setNewBranchCode] = useState('');
  /**
   * Empty until the administrator actually picks one.
   *
   * These used to be `useState(banks[0]?.name || '')`. Banks load from
   * Firestore a moment AFTER this component mounts, so at mount the list is
   * empty and the value captured was ''. The dropdown then showed the first
   * bank -- because a <select> with a value matching no option displays the
   * first one -- while holding nothing. Unless the administrator actively
   * changed the dropdown, the branch was saved with NO BANK.
   *
   * That is why branches appeared under every bank at once, and why the table
   * showed a bank nobody had chosen.
   */
  const [newBranchBank, setNewBranchBank] = useState('');
  const [newBranchZone, setNewBranchZone] = useState('');
  const [newBranchDept, setNewBranchDept] = useState('');
  const [newBranchManager, setNewBranchManager] = useState('');
  const [newBranchTarget, setNewBranchTarget] = useState(1000000);

  const selectedBank = banks.find((b) => b.id === selectedBankId) || banks[0];
  const bankDepartments = recoveryDepartments.filter((d) => !d.bankName || d.bankName === selectedBank?.name);
  // Only branches that really belong to this bank. `!br.bankName ||` used to
  // be in here, which made every branch with no bank appear under whichever
  // bank you happened to be looking at -- all four under Central Bank of
  // India, in your case.
  const bankBranches = branches.filter((br) => br.bankName === selectedBank?.name);
  /** Branches that belong to nothing, surfaced so they can be fixed. */
  const orphanBranches = branches.filter((br) => !br.bankName);

  const handleCreateBank = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBankName.trim()) return;
    addBank({
      name: newBankName.trim(),
      code: newBankCode.trim().toUpperCase() || 'BNK',
      ...(Number(newBankRate) > 0 ? { defaultCommissionRate: Number(newBankRate) } : {}),
      description: newBankDesc.trim(),
      active: true,
    });
    setIsAddBankOpen(false);
    setNewBankName('');
    setNewBankCode('');
    setNewBankDesc('');
  };

  const handleCreateDept = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    addRecoveryDepartment({
      name: newDeptName.trim(),
      code: newDeptCode.trim().toUpperCase() || 'RD',
      bankName: newDeptBankName,
      zoneName: newDeptZoneName,
      headName: newDeptHead.trim() || 'Department Head',
      description: newDeptDesc.trim(),
      active: true,
    });
    setIsAddDeptOpen(false);
    setNewDeptName('');
    setNewDeptCode('');
    setNewDeptHead('');
    setNewDeptDesc('');
  };

  const handleCreateZone = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newZoneName.trim();
    if (!name) return;
    if (!newZoneBank) {
      alert('Choose the bank this zonal office belongs to. A zone without a bank cannot be filtered, and its branches end up under the wrong bank.');
      return;
    }
    const clash = zones.find(
      (z) =>
        (z.name || '').trim().toLowerCase() === name.toLowerCase() &&
        (z.bankName || '').trim().toLowerCase() === newZoneBank.trim().toLowerCase()
    );
    if (clash) {
      alert(`"${name}" already exists under ${newZoneBank}.`);
      return;
    }
    addZone({
      name,
      code: newZoneCode.trim().toUpperCase() || 'ZON',
      headName: newZoneHead.trim() || 'Zonal Head',
      bankName: newZoneBank,
      active: true,
    });
    setIsAddZoneOpen(false);
    setNewZoneName('');
    setNewZoneCode('');
    setNewZoneHead('');
  };

  const handleCreateBranch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBranchName.trim()) return;
    // A branch with no bank belongs to nothing, shows up under every bank and
    // cannot be allocated properly. Refuse rather than save a broken record.
    if (!newBranchBank) {
      alert('Choose the bank this branch belongs to.');
      return;
    }
    if (!newBranchZone) {
      alert('Choose the zonal office this branch sits under.');
      return;
    }
    addBranch({
      name: newBranchName.trim(),
      code: newBranchCode.trim().toUpperCase() || 'BR',
      bankName: newBranchBank,
      zoneName: newBranchZone,
      departmentName: newBranchDept,
      managerName: newBranchManager.trim(),
      targetAmount: Number(newBranchTarget),
      active: true,
    });
    setIsAddBranchOpen(false);
    setNewBranchName('');
    setNewBranchCode('');
    setNewBranchManager('');
  };

  return (
    <div className="space-y-5 text-slate-800">
      {/* Header & Hierarchy Flow Diagram */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 text-blue-600 flex items-center justify-center border border-blue-200 font-bold">
              <Network className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <span>Organizational Hierarchy &amp; Access Controls</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 font-mono text-[10px] font-bold rounded-full border border-blue-200">
                  Strict RBAC Flow
                </span>
              </h2>
              <p className="text-xs text-slate-500">
                Bank → Zone → Recovery Department → Branch → User (Manager / Recovery Agent) → Customer (Accounts &amp; OTS)
              </p>
            </div>
          </div>

          {currentUser.role === 'admin' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setNewZoneBank(selectedBank?.name || banks[0]?.name || '');
                  setIsAddZoneOpen(true);
                }}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs flex items-center gap-1.5 border border-slate-200 transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Zonal Office</span>
              </button>
              <button
                onClick={() => setIsAddDeptOpen(true)}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs flex items-center gap-1.5 border border-slate-200 transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Department</span>
              </button>
              <button
                onClick={() => setIsAddBranchOpen(true)}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs flex items-center gap-1.5 border border-slate-200 transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Branch</span>
              </button>
              <button
                onClick={() => setIsAddBankOpen(true)}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 shadow-xs transition cursor-pointer"
              >
                <Building className="w-3.5 h-3.5" />
                <span>Add Bank</span>
              </button>
            </div>
          )}
        </div>

        {/* Hierarchy Chain Step Visualizer */}
        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold text-slate-600">
          <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-900 shadow-2xs">
            <Building className="w-3.5 h-3.5 text-blue-600" />
            <span>1. Bank</span>
          </div>
          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
          <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-900 shadow-2xs">
            <MapPin className="w-3.5 h-3.5 text-emerald-600" />
            <span>2. Zone</span>
          </div>
          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
          <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-900 shadow-2xs">
            <Building2 className="w-3.5 h-3.5 text-purple-600" />
            <span>3. Recovery Dept</span>
          </div>
          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
          <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-900 shadow-2xs">
            <Layers className="w-3.5 h-3.5 text-amber-600" />
            <span>4. Branch</span>
          </div>
          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
          <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-900 shadow-2xs">
            <Users className="w-3.5 h-3.5 text-indigo-600" />
            <span>5. User</span>
          </div>
          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
          <div className="flex items-center gap-1.5 bg-emerald-50 px-2.5 py-1.5 rounded-lg border border-emerald-200 text-emerald-800 font-bold shadow-2xs">
            <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>6. Customer</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl text-xs font-semibold w-fit">
        {[
          { id: 'tree', label: 'Hierarchical Tree Explorer', icon: FolderTree },
          { id: 'banks', label: 'Banks', count: banks.length, icon: Building },
          { id: 'departments', label: 'Recovery Departments', count: recoveryDepartments.length, icon: Building2 },
          { id: 'branches', label: 'Branches', count: branches.length, icon: Layers },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-3 py-2 rounded-lg transition cursor-pointer flex items-center gap-2 ${
                isActive
                  ? 'bg-white text-blue-700 shadow-xs font-bold'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                    isActive ? 'bg-blue-100 text-blue-800 font-bold' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ======================================================== */}
      {/* 1. HIERARCHICAL TREE VIEW */}
      {/* ======================================================== */}
      {activeTab === 'tree' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 text-xs">
          {/* Bank Selector Column */}
          <div className="lg:col-span-4 bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-3">
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <Building className="w-4 h-4 text-blue-600" />
              <span>Select Bank Hierarchy</span>
            </h3>
            <div className="space-y-2">
              {banks.map((b) => (
                <div
                  key={b.id}
                  className={`w-full rounded-xl border transition flex items-stretch ${
                    selectedBankId === b.id
                      ? 'bg-blue-50/80 border-blue-500 ring-1 ring-blue-500'
                      : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <button
                    onClick={() => setSelectedBankId(b.id)}
                    className="flex-1 p-3 text-left cursor-pointer flex items-center justify-between min-w-0"
                  >
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 text-sm truncate">{b.name}</div>
                      <span className="font-mono text-blue-600 text-[11px] font-semibold">{b.code}</span>
                    </div>
                    <div className="text-right shrink-0 pl-2">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold font-mono text-[11px]">
                        {b.defaultCommissionRate ? `${b.defaultCommissionRate}% Default` : 'No default rate'}
                      </span>
                    </div>
                  </button>
                  {isAdminActor && (
                    <button
                      onClick={() =>
                        runDelete(`bank "${b.name}"`, () => deleteBank(b.id), b.name)
                      }
                      title="Delete this bank"
                      className="px-2.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-r-xl transition cursor-pointer shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Hierarchy Breakdown Column */}
          <div className="lg:col-span-8 bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4">
            <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm text-slate-900">{selectedBank?.name} Structure</h3>
                <p className="text-slate-500 text-[11px]">Full drill-down of zones, departments, branches, managers, and agents</p>
              </div>
              <span className="px-2.5 py-1 bg-blue-50 text-blue-700 font-bold rounded-lg border border-blue-200 text-xs">
                Code: {selectedBank?.code}
              </span>
            </div>

            {/* Tree Branch Visualizer */}
            <div className="space-y-4 font-sans">
              {zonesOfBank(zones, selectedBank?.name).map((z) => {
                const zoneDepts = bankDepartments.filter((d) => !d.zoneName || d.zoneName === z.name);
                const zoneBranches = bankBranches.filter((br) => !br.zoneName || br.zoneName === z.name);

                return (
                  <div key={z.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-emerald-600" />
                        <span className="font-bold text-slate-900 text-sm">{z.name}</span>
                        <span className="text-[10px] text-slate-500 font-mono">({z.code})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-500">
                          {zoneBranches.length} Branches • {zoneDepts.length} Recovery Depts
                        </span>
                        {isAdminActor && (
                          <button
                            onClick={() =>
                              runDelete(`zonal office "${z.name}"`, () => deleteZone(z.id))
                            }
                            title="Delete this zonal office"
                            className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Departments under Zone */}
                    {zoneDepts.length > 0 && (
                      <div className="pl-4 border-l-2 border-purple-300 space-y-2">
                        <span className="text-[10px] text-purple-700 font-bold uppercase tracking-wider block">
                          Recovery Departments:
                        </span>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {zoneDepts.map((d) => (
                            <div key={d.id} className="p-2.5 bg-white rounded-lg border border-slate-200 space-y-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-bold text-slate-800">{d.name}</div>
                                {isAdminActor && (
                                  <button
                                    onClick={() =>
                                      runDelete(
                                        `recovery department "${d.name}"`,
                                        () => deleteRecoveryDepartment(d.id)
                                      )
                                    }
                                    title="Delete this recovery department"
                                    className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer shrink-0"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 flex justify-between">
                                <span>Head: {d.headName || 'Assigned Head'}</span>
                                <span className="font-mono text-purple-700 font-semibold">{d.code}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Branches under Zone */}
                    <div className="pl-4 border-l-2 border-blue-300 space-y-2">
                      <span className="text-[10px] text-blue-700 font-bold uppercase tracking-wider block">
                        Branches &amp; Field Staff:
                      </span>
                      <div className="space-y-2">
                        {zoneBranches.map((br) => {
                          const branchAgents = users.filter(
                            (u) => u.role === 'agent' && belongsToBranch(u, br)
                          );
                          const branchManager = users.find(
                            (u) => u.role === 'branch_manager' && belongsToBranch(u, br)
                          );

                          return (
                            <div key={br.id} className="p-3 bg-white rounded-xl border border-slate-200 space-y-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-bold text-slate-900">{br.name}</span>
                                  <span className="text-slate-500 font-mono text-[10px] ml-2">({br.code})</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">
                                    Manager: {branchManager?.name || br.managerName || 'No manager posted'}
                                  </span>
                                  {isAdminActor && (
                                    <button
                                      onClick={() =>
                                        runDelete(
                                          `branch "${br.name}"${br.bankName ? ` (${br.bankName})` : ''}`,
                                          () => deleteBranch(br.id)
                                        )
                                      }
                                      title="Delete this branch"
                                      className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                                <span className="text-[10px] text-slate-400 font-semibold">Active Agents:</span>
                                {branchAgents.length === 0 ? (
                                  <span className="text-slate-400 italic text-[11px]">No agents allocated</span>
                                ) : (
                                  branchAgents.map((ag) => (
                                    <span
                                      key={ag.id}
                                      className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded font-medium text-[10px] border border-blue-200"
                                    >
                                      {ag.name} ({ag.agentId || 'RA'})
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 2. BANKS TABLE */}
      {/* ======================================================== */}
      {activeTab === 'banks' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs text-xs">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase tracking-wider text-[11px]">
              <tr>
                <th className="p-3.5">Bank Name &amp; Code</th>
                <th className="p-3.5">Description</th>
                <th className="p-3.5 text-right">Default Commission Rate</th>
                <th className="p-3.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {banks.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50 transition">
                  <td className="p-3.5">
                    <div className="font-bold text-slate-900 text-sm">{b.name}</div>
                    <span className="font-mono text-blue-600 text-[11px] font-semibold">{b.code}</span>
                  </td>
                  <td className="p-3.5 text-slate-600">{b.description || 'Commercial Scheduled Bank'}</td>
                  <td className="p-3.5 text-right font-mono font-black text-sm text-emerald-600">
                    {b.defaultCommissionRate ? `${b.defaultCommissionRate}%` : '—'}
                  </td>
                  <td className="p-3.5 text-center">
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold text-[10px] border border-emerald-300">
                      {b.active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ======================================================== */}
      {/* 3. RECOVERY DEPARTMENTS TABLE */}
      {/* ======================================================== */}
      {activeTab === 'departments' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs text-xs">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase tracking-wider text-[11px]">
              <tr>
                <th className="p-3.5">Department Name &amp; Code</th>
                <th className="p-3.5">Assigned Bank</th>
                <th className="p-3.5">Zone</th>
                <th className="p-3.5">Department Head</th>
                <th className="p-3.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {recoveryDepartments.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50 transition">
                  <td className="p-3.5">
                    <div className="font-bold text-slate-900">{d.name}</div>
                    <span className="font-mono text-purple-700 text-[10px] font-semibold">{d.code}</span>
                  </td>
                  <td className="p-3.5 font-semibold text-slate-800">{d.bankName}</td>
                  <td className="p-3.5 text-slate-600">{d.zoneName}</td>
                  <td className="p-3.5 text-slate-700 font-medium">{d.headName}</td>
                  <td className="p-3.5 text-center">
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold text-[10px] border border-emerald-300">
                      {d.active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ======================================================== */}
      {/* 4. BRANCHES TABLE */}
      {/* ======================================================== */}
      {activeTab === 'branches' && orphanBranches.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs">
          <p className="font-bold text-amber-900">
            {orphanBranches.length} branch{orphanBranches.length === 1 ? '' : 'es'} {orphanBranches.length === 1 ? 'has' : 'have'} no bank assigned
          </p>
          <p className="text-amber-800 mt-1 leading-relaxed">
            These were saved before the bank selection was required, so they belong to
            nothing. They will not appear correctly under any bank until they are
            recreated with a bank chosen. Delete each one here and re-add it from “Add
            Branch”.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {orphanBranches.map((b) => (
              <span
                key={b.id}
                className="inline-flex items-center gap-1.5 bg-white border border-amber-300 rounded-lg pl-2.5 pr-1 py-1 font-semibold text-amber-900"
              >
                {b.name}
                {isAdminActor && (
                  <button
                    onClick={() =>
                      runDelete(`branch "${b.name}" (no bank)`, () => deleteBranch(b.id))
                    }
                    title="Delete this branch"
                    className="p-0.5 text-amber-500 hover:text-rose-600 hover:bg-rose-50 rounded transition cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'branches' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs text-xs">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase tracking-wider text-[11px]">
              <tr>
                <th className="p-3.5">Branch Name &amp; Code</th>
                <th className="p-3.5">Bank &amp; Department</th>
                <th className="p-3.5">Zone</th>
                <th className="p-3.5">Branch Manager</th>
                <th className="p-3.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {branches.map((br) => (
                <tr key={br.id} className="hover:bg-slate-50 transition">
                  <td className="p-3.5">
                    <div className="font-bold text-slate-900">{br.name}</div>
                    <span className="font-mono text-blue-600 text-[10px] font-semibold">{br.code}</span>
                  </td>
                  <td className="p-3.5 text-[11px]">
                    {/* No invented names. A branch with no bank said "State
                        Bank of India", and one with no department said
                        "Recovery Cell" -- while the Recovery Departments count
                        read 0. The screen was describing data that was not
                        there. */}
                    {br.bankName ? (
                      <span className="font-semibold text-slate-800 block">{br.bankName}</span>
                    ) : (
                      <span className="font-bold text-amber-700 block">No bank assigned</span>
                    )}
                    <span className="text-slate-500">{br.departmentName || '—'}</span>
                  </td>
                  <td className="p-3.5 text-slate-600">{br.zoneName}</td>
                  {/* The posted branch manager, matched on the branch RECORD
                      rather than on its name -- two banks can both have a
                      Bidkin. Falls back to the name typed when the branch was
                      created, and says plainly when there is nobody. */}
                  <td className="p-3.5 text-slate-700 font-medium">
                    {(() => {
                      const posted = users.find(
                        (u) => u.role === 'branch_manager' && belongsToBranch(u, br)
                      );
                      const shown = posted?.name || (br.managerName || '').trim();
                      return shown ? (
                        shown
                      ) : (
                        <span className="text-slate-400 italic font-normal">No manager posted</span>
                      );
                    })()}
                  </td>
                  <td className="p-3.5 text-center">
                    {/* Green for everything, whatever it said. Branches were
                        never given an active flag when created, so every row
                        read INACTIVE in a green badge. */}
                    <span
                      className={`px-2 py-0.5 rounded-full font-bold text-[10px] border ${
                        br.active !== false
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                          : 'bg-slate-200 text-slate-600 border-slate-300'
                      }`}
                    >
                      {br.active !== false ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: ADD ZONAL OFFICE */}
      {/* ======================================================== */}
      {isAddZoneOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] text-xs">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900">Add Zonal Office</h3>
              <button
                onClick={() => setIsAddZoneOpen(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateZone} className="p-6 space-y-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Bank *:</label>
                <select
                  value={newZoneBank}
                  onChange={(e) => setNewZoneBank(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">Select bank…</option>
                  {banks.map((b) => (
                    <option key={`az-bank-${b.id}`} value={b.name}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  A zonal office belongs to exactly one bank. Without this, its
                  branches cannot be filtered by bank.
                </p>
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1">
                  Zonal Office Name *:
                </label>
                <input
                  type="text"
                  value={newZoneName}
                  onChange={(e) => setNewZoneName(e.target.value)}
                  placeholder="e.g. Chhatrapati Sambhajinagar Zone"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Code:</label>
                  <input
                    type="text"
                    value={newZoneCode}
                    onChange={(e) => setNewZoneCode(e.target.value)}
                    placeholder="CSN"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 font-mono focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Zonal Head:</label>
                  <input
                    type="text"
                    value={newZoneHead}
                    onChange={(e) => setNewZoneHead(e.target.value)}
                    placeholder="Optional"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddZoneOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Create Zonal Office</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: ADD BANK */}
      {/* ======================================================== */}
      {isAddBankOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] text-xs">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900">Add New Banking Institution</h3>
              <button onClick={() => setIsAddBankOpen(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateBank} className="p-6 space-y-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Bank Name *:</label>
                <input
                  type="text"
                  value={newBankName}
                  onChange={(e) => setNewBankName(e.target.value)}
                  placeholder="e.g. Bank of Baroda"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Bank Code *:</label>
                  <input
                    type="text"
                    value={newBankCode}
                    onChange={(e) => setNewBankCode(e.target.value)}
                    placeholder="e.g. BOB"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 uppercase focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Default Rate (%) <span className="font-normal text-slate-400">optional</span>:</label>
                  <AmountInput
                    value={newBankRate}
                    onValueChange={setNewBankRate}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Description:</label>
                <input
                  type="text"
                  value={newBankDesc}
                  onChange={(e) => setNewBankDesc(e.target.value)}
                  placeholder="e.g. Public Sector Scheduled Bank"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddBankOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl border border-slate-200 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 font-bold rounded-xl text-white shadow-xs cursor-pointer"
                >
                  Save Bank
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: ADD RECOVERY DEPARTMENT */}
      {/* ======================================================== */}
      {isAddDeptOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] text-xs">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900">Add Recovery Department</h3>
              <button onClick={() => setIsAddDeptOpen(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateDept} className="p-6 space-y-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Department Name *:</label>
                <input
                  type="text"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  placeholder="e.g. SAMB Stressed Asset Recovery Cell"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Bank *:</label>
                  <select
                    value={newDeptBankName}
                    onChange={(e) => setNewDeptBankName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select bank…</option>
                    {banks.map((b) => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Zone *:</label>
                  <select
                    value={newDeptZoneName}
                    onChange={(e) => setNewDeptZoneName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select zonal office…</option>
                    {zonesOfBank(zones, newDeptBankName).map((z) => (
                      <option key={z.id} value={z.name}>{z.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Dept Code:</label>
                  <input
                    type="text"
                    value={newDeptCode}
                    onChange={(e) => setNewDeptCode(e.target.value)}
                    placeholder="e.g. RD-PUN"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 uppercase focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Department Head:</label>
                  <input
                    type="text"
                    value={newDeptHead}
                    onChange={(e) => setNewDeptHead(e.target.value)}
                    placeholder="e.g. Rajesh Shinde"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddDeptOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl border border-slate-200 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 font-bold rounded-xl text-white shadow-xs cursor-pointer"
                >
                  Save Department
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: ADD BRANCH */}
      {/* ======================================================== */}
      {isAddBranchOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] text-xs">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900">Add Operating Branch</h3>
              <button onClick={() => setIsAddBranchOpen(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateBranch} className="p-6 space-y-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Branch Name *:</label>
                <input
                  type="text"
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  placeholder="e.g. Pune Camp Retail Hub"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Bank *:</label>
                  <select
                    value={newBranchBank}
                    onChange={(e) => {
                      setNewBranchBank(e.target.value);
                      // A different bank means a different set of zones.
                      setNewBranchZone('');
                    }}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select bank…</option>
                    {banks.map((b) => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Zone *:</label>
                  <select
                    value={newBranchZone}
                    onChange={(e) => setNewBranchZone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">
                      {newBranchBank ? 'Select zonal office…' : 'Pick a bank first'}
                    </option>
                    {zonesOfBank(zones, newBranchBank).map((z) => (
                      <option key={z.id} value={z.name}>{z.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Branch Code:</label>
                  <input
                    type="text"
                    value={newBranchCode}
                    onChange={(e) => setNewBranchCode(e.target.value)}
                    placeholder="e.g. BR-PUN-03"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 uppercase focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Branch Manager:</label>
                  <input
                    type="text"
                    value={newBranchManager}
                    onChange={(e) => setNewBranchManager(e.target.value)}
                    placeholder="e.g. Sunita Deshmukh"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddBranchOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl border border-slate-200 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 font-bold rounded-xl text-white shadow-xs cursor-pointer"
                >
                  Save Branch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
